import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { fetchWithAuth } from '../../../../js/authToken';
import API_BASE_URL from '../../../../js/urlHelper';

const fields = [
  { key: 'nombre', label: 'NOMBRE(S)', type: 'text', autoComplete: 'given-name' },
  { key: 'apellidoPaterno', label: 'APELLIDO PATERNO', type: 'text', autoComplete: 'family-name' },
  { key: 'apellidoMaterno', label: 'APELLIDO MATERNO', type: 'text', autoComplete: 'family-name' },
  { key: 'apellidoConyuge', label: 'APELLIDO CÓNYUGE (OPCIONAL)', type: 'text', placeholder: 'Opcional', autoComplete: 'off' },
  {
    key: 'estadoCivil',
    label: 'ESTADO CIVIL',
    type: 'select',
    options: [
      { value: '', label: 'SELECCIONE...' },
      { value: 'SOLTERO/A', label: 'SOLTERO/A' },
      { value: 'CONVIVIENTE', label: 'CONVIVIENTE' },
      { value: 'CASADO/A', label: 'CASADO/A' },
      { value: 'VIUDO/A', label: 'VIUDO/A' },
      { value: 'DIVORCIADO/A', label: 'DIVORCIADO/A' },
    ],
  },
  { key: 'dni', label: 'DNI/CARNET DE EXTRANJERÍA', type: 'text', placeholder: '8-9 DÍGITOS', autoComplete: 'off', inputMode: 'numeric', maxLength: 9 },
  { key: 'fechaCaducidadDni', label: 'CADUCIDAD (DNI/CARNET)', type: 'date', placeholder: '', autoComplete: 'off' },
  { key: 'ruc', label: 'RUC', type: 'text', placeholder: '11 DÍGITOS', autoComplete: 'off', inputMode: 'numeric', maxLength: 11 },
];

const LOOKUP_IDLE_STATE = {
  status: 'idle',
  message: '',
};

const LOOKUP_STATUS_STYLES = {
  fallback: 'border-amber-200 bg-amber-50 text-amber-700',
  error: 'border-red-200 bg-red-50 text-red-700',
};

const IDENTITY_LOOKUP_FIELDS = new Set(['nombre', 'apellidoPaterno', 'apellidoMaterno']);
const LOOKUP_FALLBACK_STATUSES = new Set(['fallback', 'error']);
const LOOKUP_REQUIRED_MESSAGE = 'Presiona la lupa para validar el documento.';

const EMPTY_IDENTITY_DATA = {
  nombre: '',
  apellidoPaterno: '',
  apellidoMaterno: '',
};

function normalizeErrorMessage(error) {
  if (Array.isArray(error)) {
    return error.filter(Boolean).join(', ');
  }

  if (typeof error === 'string') {
    return error;
  }

  return '';
}

function normalizeLookupMessage(status, message, isExtranjeria) {
  const manualSuffix = 'Puedes completar o corregir los nombres manualmente.';

  if (status === 404) {
    return `No se encontraron datos para este ${isExtranjeria ? 'carnet de extranjería' : 'DNI'}. ${manualSuffix}`;
  }

  if (status === 429) {
    return `${message || 'Se alcanzó el límite de consultas del proveedor.'} ${manualSuffix}`;
  }

  if (message) {
    return `${message} ${manualSuffix}`;
  }

  return `No se pudo consultar el documento en este momento. ${manualSuffix}`;
}

function hasCompleteIdentityData(data) {
  return Boolean(
    String(data?.nombres || '').trim() &&
    String(data?.apellido_paterno || '').trim() &&
    String(data?.apellido_materno || '').trim()
  );
}

async function parseLookupPayload(response, fallbackMessage) {
  const contentType = response.headers.get('content-type') || '';

  if (!contentType.includes('application/json')) {
    return {
      message: fallbackMessage,
      data: null,
    };
  }

  try {
    const payload = await response.json();
    return {
      message: payload?.message || fallbackMessage,
      data: payload?.data || null,
    };
  } catch {
    return {
      message: fallbackMessage,
      data: null,
    };
  }
}

const DatosUsuarioBase = ({ datos, errors = {}, onChange, documentLookup = null }) => {
  const [isExtranjeria, setIsExtranjeria] = useState(false);
  const [documentTypeTouched, setDocumentTypeTouched] = useState(false);
  const [localErrors, setLocalErrors] = useState({});
  const [lookupState, setLookupState] = useState(LOOKUP_IDLE_STATE);
  const previousDocumentRef = useRef('');
  const previousLookupKeyRef = useRef('');
  const latestLookupKeyRef = useRef('');
  const [verifiedLookupKey, setVerifiedLookupKey] = useState('');

  const requiredDigits = useMemo(() => (isExtranjeria ? 9 : 8), [isExtranjeria]);
  const sanitizedDocument = useMemo(
    () => String(datos?.dni || '').replace(/[^\d]/g, ''),
    [datos?.dni]
  );
  const currentLookupKey = useMemo(
    () => `${isExtranjeria ? 'cee' : 'dni'}:${sanitizedDocument}`,
    [isExtranjeria, sanitizedDocument]
  );
  const lookupEnabled = Boolean(documentLookup?.enabled);
  const requireLookupBeforeManualEntry = Boolean(
    lookupEnabled && documentLookup?.requireLookupBeforeManualEntry
  );
  const lookupButtonLabel = isExtranjeria ? 'Buscar CEE' : 'Buscar DNI';
  const manualIdentityEntryAllowed =
    !requireLookupBeforeManualEntry || LOOKUP_FALLBACK_STATUSES.has(lookupState.status);
  const shouldLockIdentityFields = requireLookupBeforeManualEntry && !manualIdentityEntryAllowed;
  const lookupRequiredError =
    requireLookupBeforeManualEntry &&
    sanitizedDocument.length === requiredDigits &&
    verifiedLookupKey !== currentLookupKey &&
    !manualIdentityEntryAllowed
      ? LOOKUP_REQUIRED_MESSAGE
      : '';
  const isLookupDisabled =
    !lookupEnabled ||
    documentLookup?.disabled ||
    lookupState.status === 'loading' ||
    sanitizedDocument.length !== requiredDigits;

  const hasFieldError = (fieldKey) =>
    Boolean(getError(`datos.${fieldKey}`)) ||
    Boolean(lookupRequiredError && IDENTITY_LOOKUP_FIELDS.has(fieldKey));

  const inputClass = (fieldKey) =>
    `w-full rounded-xl border bg-white px-3 py-2.5 text-sm text-slate-900 transition focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200 ${
      hasFieldError(fieldKey) ? 'border-red-500 ring-2 ring-red-100' : 'border-slate-300'
    } disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-500`;
  const documentInputClass = `${inputClass('dni')} ${lookupEnabled ? 'pr-14' : ''}`;

  function getError(name) {
    if (name === 'datos.dni' && !localErrors[name] && !errors[name] && lookupRequiredError) {
      return lookupRequiredError;
    }

    const directError = normalizeErrorMessage(localErrors[name] || errors[name]);
    if (directError) {
      return directError;
    }

    const [section, field] = name.split('.');
    return normalizeErrorMessage(errors?.[section]?.[field]);
  }

  const validateDni = (value, len) => {
    const name = 'datos.dni';
    if (!value || value.length === 0) {
      setLocalErrors((prev) => ({ ...prev, [name]: '' }));
      return;
    }

    if (!/^\d+$/.test(value)) {
      setLocalErrors((prev) => ({ ...prev, [name]: 'Solo se permiten dígitos.' }));
      return;
    }

    if (value.length !== len) {
      setLocalErrors((prev) => ({
        ...prev,
        [name]: `Debe tener exactamente ${len} dígitos.`,
      }));
      return;
    }

    setLocalErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleUppercaseChange = (key, e) => {
    let value = e.target.value;

    if (key === 'dni' || key === 'ruc') {
      value = value.replace(/[^\d]/g, '');
    } else if (key !== 'fechaCaducidadDni' && key !== 'estadoCivil') {
      value = value.toUpperCase();
    }

    if (key === 'dni') {
      if (value.length > requiredDigits) value = value.slice(0, requiredDigits);
      validateDni(value, requiredDigits);
    } else if (shouldLockIdentityFields && IDENTITY_LOOKUP_FIELDS.has(key)) {
      return;
    }

    onChange(e.target.name, value);
  };

  const handleLookup = async () => {
    if (isLookupDisabled) {
      return;
    }

    const endpoint = isExtranjeria ? 'cee' : 'dni';
    const payloadKey = isExtranjeria ? 'cee' : 'dni';
    const requestLookupKey = currentLookupKey;
    const fallbackMessage = 'No se recibió una respuesta válida del servicio de consulta.';

    setLookupState({
      status: 'loading',
      message: `Consultando ${isExtranjeria ? 'carnet de extranjería' : 'DNI'}...`,
    });

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/consultas/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          [payloadKey]: sanitizedDocument,
        }),
      });

      const { message, data } = await parseLookupPayload(response, fallbackMessage);
      if (latestLookupKeyRef.current !== requestLookupKey) {
        return;
      }

      if (!response.ok) {
        setVerifiedLookupKey('');
        setLookupState({
          status: 'fallback',
          message: normalizeLookupMessage(response.status, message, isExtranjeria),
        });
        return;
      }

      if (!hasCompleteIdentityData(data)) {
        setVerifiedLookupKey('');
        setLookupState({
          status: 'fallback',
          message: normalizeLookupMessage(response.status, fallbackMessage, isExtranjeria),
        });
        return;
      }

      onChange('datos.nombre', String(data?.nombres || '').toUpperCase());
      onChange('datos.apellidoPaterno', String(data?.apellido_paterno || '').toUpperCase());
      onChange('datos.apellidoMaterno', String(data?.apellido_materno || '').toUpperCase());

      setVerifiedLookupKey(requestLookupKey);
      setLookupState(LOOKUP_IDLE_STATE);
    } catch (error) {
      if (latestLookupKeyRef.current !== requestLookupKey) {
        return;
      }

      setVerifiedLookupKey('');
      setLookupState({
        status: 'fallback',
        message: normalizeLookupMessage(0, error?.message || '', isExtranjeria),
      });
    }
  };

  useEffect(() => {
    const previousDocument = previousDocumentRef.current;

    if (!documentTypeTouched && previousDocument === '' && sanitizedDocument.length === 9) {
      setIsExtranjeria(true);
    }

    if (sanitizedDocument === '' && previousDocument !== '') {
      setIsExtranjeria(false);
      setDocumentTypeTouched(false);
      setLookupState(LOOKUP_IDLE_STATE);
    }

    previousDocumentRef.current = sanitizedDocument;
  }, [documentTypeTouched, sanitizedDocument]);

  useEffect(() => {
    latestLookupKeyRef.current = currentLookupKey;

    if (previousLookupKeyRef.current && previousLookupKeyRef.current !== currentLookupKey) {
      setLookupState(LOOKUP_IDLE_STATE);
      setVerifiedLookupKey('');

      if (requireLookupBeforeManualEntry) {
        Object.entries(EMPTY_IDENTITY_DATA).forEach(([field, value]) => {
          onChange(`datos.${field}`, value);
        });
      }
    }

    previousLookupKeyRef.current = currentLookupKey;
  }, [currentLookupKey, onChange, requireLookupBeforeManualEntry]);

  useEffect(() => {
    validateDni(sanitizedDocument, requiredDigits);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredDigits, sanitizedDocument]);

  return (
    <div className="ui-card p-5 md:p-6">
      <div className="mb-4 flex items-center gap-3 border-b border-slate-200 pb-3">
        <div className="h-8 w-1.5 rounded-full bg-red-600" />
        <h3 className="text-base font-semibold uppercase tracking-[0.08em] text-slate-800">DATOS PERSONALES</h3>
      </div>

      <div className="mb-4 flex items-center">
        <input
          id="check-extranjeria"
          type="checkbox"
          checked={isExtranjeria}
          onChange={(e) => {
            setDocumentTypeTouched(true);
            setIsExtranjeria(e.target.checked);
          }}
          className="mr-2"
        />
        <label htmlFor="check-extranjeria" className="text-sm font-medium">
          Carnet de Extranjería (9 dígitos)
        </label>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div>
          <label htmlFor="datos-dni" className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
            DNI/CARNET DE EXTRANJERÍA
          </label>
          <div className="relative">
            <input
              id="datos-dni"
              name="datos.dni"
              type="text"
              value={datos?.dni || ''}
              placeholder={isExtranjeria ? '9 DÍGITOS' : '8 DÍGITOS'}
              autoComplete="off"
              inputMode="numeric"
              maxLength={requiredDigits}
              onChange={(e) => handleUppercaseChange('dni', e)}
              className={documentInputClass}
              style={{ textTransform: 'none' }}
              onBlur={(e) => validateDni(e.target.value, requiredDigits)}
            />
            {lookupEnabled && (
              <button
                type="button"
                onClick={handleLookup}
                disabled={isLookupDisabled}
                title={lookupButtonLabel}
                aria-label={lookupButtonLabel}
                className="absolute right-1.5 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-200 disabled:cursor-not-allowed disabled:text-slate-300"
              >
                {lookupState.status === 'loading' ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Search className="h-4 w-4" aria-hidden="true" />
                )}
              </button>
            )}
          </div>
          {getError('datos.dni') && (
            <p className="mt-1 text-xs font-medium text-red-600">{getError('datos.dni')}</p>
          )}
          {lookupEnabled && ['fallback', 'error'].includes(lookupState.status) && (
            <div
              className={`mt-2 rounded-xl border px-3 py-2 text-xs font-medium ${LOOKUP_STATUS_STYLES[lookupState.status] || LOOKUP_STATUS_STYLES.error}`}
            >
              {lookupState.message}
            </div>
          )}
        </div>

        {fields
          .filter((field) => field.key !== 'dni')
          .map(({ key, label, type, options, placeholder, autoComplete, inputMode, maxLength }) => (
            <div key={key}>
              <label htmlFor={`datos-${key}`} className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                {label}
              </label>

              {type === 'select' ? (
                <select
                  id={`datos-${key}`}
                  name={`datos.${key}`}
                  value={datos?.[key] || ''}
                  onChange={(e) => handleUppercaseChange(key, e)}
                  className={inputClass(key)}
                  autoComplete={autoComplete}
                >
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id={`datos-${key}`}
                  name={`datos.${key}`}
                  type={type}
                  value={datos?.[key] || ''}
                  placeholder={placeholder}
                  autoComplete={autoComplete}
                  inputMode={inputMode}
                  maxLength={maxLength}
                  onChange={(e) => handleUppercaseChange(key, e)}
                  className={inputClass(key)}
                  disabled={shouldLockIdentityFields && IDENTITY_LOOKUP_FIELDS.has(key)}
                  style={
                    type === 'text' && key !== 'dni' && key !== 'ruc'
                      ? { textTransform: 'uppercase' }
                      : { textTransform: 'none' }
                  }
                />
              )}

              {getError(`datos.${key}`) && (
                <p className="mt-1 text-xs font-medium text-red-600">{getError(`datos.${key}`)}</p>
              )}
            </div>
          ))}
      </div>
    </div>
  );
};

export default DatosUsuarioBase;